import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/Icon';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import BottomSheet from '../ui/BottomSheet';
import { navigateFromLayer } from '../backStack';
import { navGroupsFor } from '../navModel';

/**
 * «المزيد» — everything that is not on the tab bar, as a full-height sheet: who you are,
 * a filter box (admins have ~30 destinations), the destinations by section, language, theme,
 * sign out. Replaces the old side drawer on phones.
 */
interface Props {
  open: boolean;
  onClose: () => void;
}

const MoreSheet: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');

  const groups = useMemo(() => navGroupsFor(user), [user]);
  const q = query.trim().toLowerCase();
  const label = (key: string, fallback?: string) => String(t(key, { defaultValue: fallback || key }));
  const visible = groups
    .map((g) => ({ ...g, items: g.items.filter((it) => !q || label(it.labelKey, it.fallback).toLowerCase().includes(q)) }))
    .filter((g) => g.items.length > 0);

  const go = (to: string) => {
    onClose();
    setQuery('');
    navigateFromLayer(navigate, to);
  };
  const isActive = (to: string) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`));

  if (!user) return null;
  const roleKey = String(user.role).toLowerCase();
  const initial = (user.username || '?').trim().charAt(0).toUpperCase();

  return (
    <BottomSheet open={open} onClose={onClose} full ariaLabel={String(t('bottomNav.more'))}>
      <div className="m-more">
        <div className="m-more-profile">
          <span className="m-avatar" aria-hidden="true">{initial}</span>
          <div className="m-more-who">
            <div className="m-more-name" dir="auto">{user.username}</div>
            <div className="m-more-role">
              {String(t(`roles.${roleKey}`, { defaultValue: user.role }))}
              {user.workshopName ? <span dir="auto"> · {user.workshopName}</span> : null}
            </div>
          </div>
        </div>

        <label className="m-search">
          <Icon name="search" size={18} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={String(t('mobile.findInMenu', 'ابحث في القائمة'))}
            aria-label={String(t('mobile.findInMenu', 'ابحث في القائمة'))}
            enterKeyHint="search"
          />
        </label>

        {visible.map((g, gi) => (
          <section key={g.titleKey || `g${gi}`} className="m-list-group">
            {g.titleKey && <h2 className="m-list-title">{label(g.titleKey)}</h2>}
            <div className="m-list">
              {g.items.map((it) => (
                <button
                  key={`${it.to}-${it.labelKey}`}
                  type="button"
                  className={`m-list-row${isActive(it.to) ? ' is-current' : ''}`}
                  onClick={() => go(it.to)}
                >
                  <span className="m-list-icon"><Icon name={it.icon} size={20} /></span>
                  <span className="m-list-label">{label(it.labelKey, it.fallback)}</span>
                  <Icon name="chevron" size={18} className="m-list-chevron" />
                </button>
              ))}
            </div>
          </section>
        ))}
        {visible.length === 0 && <p className="m-empty-line">{String(t('mobile.noMatches', 'لا توجد نتائج'))}</p>}

        <section className="m-list-group">
          <h2 className="m-list-title">{String(t('mobile.settings', 'الإعدادات'))}</h2>
          <div className="m-list">
            <div className="m-list-row is-static">
              <span className="m-list-icon"><Icon name="globe" size={20} /></span>
              <span className="m-list-label">{String(t('mobile.language', 'اللغة'))}</span>
              <div className="m-seg" role="radiogroup" aria-label={String(t('mobile.language', 'اللغة'))}>
                <button type="button" role="radio" aria-checked={language === 'ar'} className={language === 'ar' ? 'is-on' : ''} onClick={() => changeLanguage('ar')}>العربية</button>
                <button type="button" role="radio" aria-checked={language !== 'ar'} className={language !== 'ar' ? 'is-on' : ''} onClick={() => changeLanguage('en')}>English</button>
              </div>
            </div>
            <div className="m-list-row is-static">
              <span className="m-list-icon"><Icon name={theme === 'dark' ? 'moon' : 'sun'} size={20} /></span>
              <span className="m-list-label">{String(t('mobile.appearance', 'المظهر'))}</span>
              <div className="m-seg" role="radiogroup" aria-label={String(t('mobile.appearance', 'المظهر'))}>
                <button type="button" role="radio" aria-checked={theme === 'dark'} className={theme === 'dark' ? 'is-on' : ''} onClick={() => setTheme('dark')}>{String(t('mobile.dark', 'داكن'))}</button>
                <button type="button" role="radio" aria-checked={theme === 'light'} className={theme === 'light' ? 'is-on' : ''} onClick={() => setTheme('light')}>{String(t('mobile.light', 'فاتح'))}</button>
              </div>
            </div>
          </div>
        </section>

        <button
          type="button"
          className="m-signout"
          onClick={() => {
            onClose();
            logout();
          }}
        >
          <Icon name="logout" size={20} />
          {String(t('common.logout'))}
        </button>
      </div>
    </BottomSheet>
  );
};

export default MoreSheet;
